<template>
    <div class="event-screen title w-100 h-100 d-flex flex-column align-items-center justify-content-start" v-if="$store.state.game.question">
        <div class="custom-background" :style="{ backgroundImage: `url('${$store.state.game.images?.activity_image}')`}"></div>

        <FullScreen btn_class="btn btn-sm btn-outline-light event-icon-btn position-absolute top-0 start-0 m-3 title-logout" />
        <button type="button" class="btn btn-sm btn-outline-light position-absolute top-0 end-0 m-3 title-logout" title="Cerrar sesión" @click="logout"><i class="bi bi-box-arrow-left"></i></button>

        <div class="title-layout">
            <div class="title-progress-block mb-4">
                <StageProgress v-if="progress" :types="progress.types" :index="progress.index" :color="own_jersey_color || undefined" />
                <div class="title-segment" v-if="current_question.question.segment"><i class="bi bi-signpost-split-fill me-1"></i>{{ utils.get_segment_label(current_question.question.segment) }}</div>
            </div>

            <div class="title-copy">
                <div class="title-question-label">{{ utils.is_content_activity(current_question.question.type) ? 'Actividad' : 'Pregunta' }} {{ progress ? progress.index + 1 : '' }}</div>
                <div class="title-question-text" v-html="format_title(current_question.question.title)"></div>
            </div>

            <!-- Always in the DOM (not v-if on start_time) so its reserved
                 space never appears/disappears under the content above -
                 only what's drawn inside it changes. Kept small/muted - the
                 question above is what should hold attention, this is a
                 supporting cue, not the main event. -->
            <div class="title-timer mt-4" id="clock-container">
                <svg width="180" height="180" viewBox="0 0 100 100" class="title-timer-ring">
                    <circle cx="50" cy="50" r="42" fill="none" stroke-width="6" class="title-timer-track"/>
                    <circle cx="50" cy="50" r="42" fill="none" stroke-width="6" stroke-linecap="round" class="title-timer-progress"
                        :stroke-dasharray="ring_circumference"
                        :stroke-dashoffset="ring_circumference * ring_percent"
                        transform="rotate(-90 50 50)"/>
                </svg>
                <!-- "PREPARADOS/LISTOS/¡YA!" replaces the digits in this same
                     spot (on the clock, not a full-screen overlay) - the
                     Transition just softens each swap, nothing else on
                     screen is affected. -->
                <Transition name="ready-fade" mode="out-in">
                    <div v-if="countdown.minutes != null && countdown.seconds != null" key="clock" class="title-timer-text">
                        <h1 class="m-0 clock-text">{{ countdown.minutes === '00' ? Number(countdown.seconds) : countdown.minutes + ':' + countdown.seconds }}</h1>
                    </div>
                    <div v-else-if="game_state" :key="game_state" class="title-timer-text">
                        <h2 class="m-0 ready-text">{{ game_state }}</h2>
                    </div>
                    <div v-else key="idle" class="title-timer-text">
                        <h1 class="m-0 clock-text clock-text-idle">--:--</h1>
                    </div>
                </Transition>
                <span class="title-timer-caption">Prepárate</span>
            </div>
        </div>

        <!-- Sponsor logo always on the right, jersey always on the left -
             consistent with Activity.vue/Result.vue's footer layout. -->
        <EventBrand class="event-title-brand" />

        <div v-if="own_jersey_color" class="position-absolute start-0 ms-4 d-flex flex-column align-items-center title-jersey-card">
            <div class="title-jersey-card-label">Equipo</div>
            <TeamJerseyBadge :color="own_jersey_color" :size="38" />
            <div class="title-jersey-card-name" v-if="$store.state.game.user">{{ $store.state.game.user.username.toUpperCase() }}</div>
        </div>

        <div class="modal fade p-3" id="previewImageModal" tabindex="-1" aria-labelledby="previewImageModal" aria-hidden="true">
            <div class="modal-dialog modal-fullscreen w-100 h-100">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5 text-black" id="previewImageModal">Imagen</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body bg-dark bg-opacity-25 d-flex justify-content-center align-items-center">
                        <div class="w-100 h-100 d-flex justify-content-center align-items-start">
                            <img class="img-fluid" :src="preview_img">
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FullScreen from '@/components/FullScreen.vue'
import EventBrand from '@/components/EventBrand.vue'
import { Modal } from 'bootstrap'
import TeamJerseyBadge from '@/components/TeamJerseyBadge.vue'
import StageProgress from '@/components/StageProgress.vue'
import { stageProgress } from '@/utils/stage-progress'

export default {
    components: { FullScreen,
        EventBrand,
        TeamJerseyBadge,
        StageProgress
    },
    data(){
        return {
            utils: require('../../../shared/utils'),
            interval: null,
            ring_circumference: 2 * Math.PI * 42,
            ring_percent: 0,
            countdown_total: null,
            last_sync_attempt: null,
            countdown: {
                minutes: null,
                seconds: null
            },
            game_state: "",
            preview_img: null,
            img_preview: false,
        };
    },
    watch : {
        // Watching the whole `question` object refires on every
        // CLIENT_UPDATE broadcast (client.js rebuilds it from scratch every
        // time, whether or not anything relevant changed). Watching
        // current_question isn't enough either: the server sets
        // start_time to null when a question is first launched (LANZAR /
        // launch_title) and only fills in the real value once the timed
        // countdown actually begins (COMENZAR / launch_activity) - same
        // question id both times, so a current_question watcher never
        // fires for that second step and the ring never starts. Watching
        // the start_time value itself catches both the null and the real
        // value, and Vue's dot-path watcher compares the primitive itself
        // rather than any wrapping object's identity, so it's immune to
        // the reference-churn problem too.
        '$store.state.game.question.question.start_time' : function(){
            this.prepare_countdown();
        }
    },
    computed : {
        current_question : function(){
            return this.$store.state.game.question;
        },
        // Where we are in the current stage - see utils/stage-progress.js.
        progress(){
            return stageProgress(this.$store.state.game);
        },
        own_jersey_color(){
            const user = this.$store.state.game.user;
            return (user && user.avatar_style && user.avatar_style.color) || null;
        }
    },
    methods : {   
        toggle_imgpreview(){
            this.img_preview = !this.img_preview;
        },     
        format_title(title){
            // Expresión regular para encontrar enlaces en el texto
            const regex = /https?:\/\/[^\s]+/g;

            // Reemplazar enlaces en el texto con botones
            const textoConBoton = title.replace(regex, (match) => {
                this.preview_img = match;
                return `<button type="button" class="btn btn-sm btn-primary" data-bs-toggle="modal" data-bs-target="#previewImageModal">ver imagen</button>`;
            });

            return textoConBoton;
        },
        logout(){
            this.utils.reset_client();
        },
        prepare_countdown: function(){
            if(this.interval != null) clearInterval(this.interval);
            this.interval = null;
            this.countdown = {minutes: null, seconds: null};
            this.game_state = "";
            this.ring_percent = 0;
            this.last_sync_attempt = null;
            const start = this.$store.state.game.question?.question?.start_time;
            if(start == null) return;
            this.countdown_total = Math.max(0, start - Date.now());
            this.update_countdown();
            this.interval = setInterval(this.update_countdown, 100);
        },
        update_countdown: function(){
            const game = this.$store.state.game;
            const start = game.question?.question?.start_time;
            if(start == null) return;
            const now = Date.now();
            const remaining = start - now;
            this.ring_percent = this.countdown_total > 0
                ? Math.max(0, Math.min(1, 1 - remaining / this.countdown_total)) : 1;
            if(remaining <= 0){
                this.countdown = {minutes: null, seconds: null};
                this.game_state = '¡YA!';
                // A suspended tab can skip every countdown tick. Recover only this
                // client; never navigate from a timer or trigger a global broadcast.
                if(game.state === this.utils.GAME_STATES.TITLE
                    && (this.last_sync_attempt == null || now - this.last_sync_attempt >= 2000)){
                    this.last_sync_attempt = now;
                    window.network.send(window.RPC.REQUEST_SYNC, null);
                }
                return;
            }
            if(remaining >= 3000){
                this.game_state = '';
                this.countdown = {
                    minutes: String(Math.floor(remaining / 60000)).padStart(2, '0'),
                    seconds: String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0'),
                };
            }else{
                this.countdown = {minutes: null, seconds: null};
                this.game_state = remaining >= 2000 ? 'PREPARADOS' : remaining >= 1000 ? 'LISTOS' : '¡YA!';
            }
        }

    },
    mounted(){
        console.log("Title.vue mounted")

        if(window.store.state.network == "connected")
            window.network.wso.send(JSON.stringify({action: window.RPC.UPDATE_VIEW, params: {view: window.router.currentRoute._value.name, activity: (window.store.state.game.question) ? window.store.state.game.question.id : null}}))
            
        this.prepare_countdown();

        $('#preview-img').click(function(){
            this.toggle_imgpreview();
        }.bind(this));

        $('.modal-backdrop').remove();
    },
    unmounted(){
        if(this.interval != null){
            clearInterval(this.interval);
        }
    }
}
</script>

<style scoped>
    .title{
        user-select: none;
        overflow: hidden !important;
        background: radial-gradient(circle at 50% 20%, var(--vc-ink-700), var(--vc-ink-900) 65%);
        color: var(--vc-paper-50);
        padding-top: 8vh;
    }


    .title-progress-block{
        width: min(560px, 86vw);
    }

    .title-segment{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(255, 255, 255, 0.7);
        margin-top: 6px;
    }

    .title-question-label{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: rgba(255, 255, 255, 0.55);
    }

    /* This is what should hold attention - bigger/bolder than the clock
       below it, which is a supporting cue rather than the main event. */
    .title-question-text{
        font-size: 2.1rem;
        font-weight: 800;
        margin-top: 6px;
        max-width: 46vw;
    }

    /* 46vw wraps text awkwardly on a narrow screen (a lot less absolute
       width to work with) - let it use most of the width there instead. */
    @media (max-width: 768px){
        .title-question-text{
            max-width: 92vw;
        }
    }

    .title-timer{
        position: relative;
        width: 180px;
        height: 180px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.85;
    }

    .title-timer-ring{
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .title-timer-track{
        stroke: rgba(255, 255, 255, 0.15);
    }

    .title-timer-progress{
        stroke: var(--vc-accent-500);
        transition: stroke-dashoffset 0.1s linear;
    }

    .title-timer-text{
        position: relative;
        text-align: center;
        padding: 0 12px;
    }

    .clock-text{
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: 1.7rem;
        color: var(--vc-paper-50);
    }

    .clock-text-idle{
        opacity: 0.35;
    }

    /* "PREPARADOS/LISTOS/¡YA!" - sized to fit the same clock box the digits
       use, not a full-screen takeover. */
    .ready-text{
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: 1.3rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--vc-accent-500);
    }

    .ready-fade-enter-active,
    .ready-fade-leave-active{
        transition: opacity 0.25s ease;
    }
    .ready-fade-enter-from,
    .ready-fade-leave-to{
        opacity: 0;
    }

    /* Same floating-card treatment as Activity.vue's countdown timer -
       hovering above the bottom edge instead of flush against it. */
    .title-jersey-card{
        bottom: 40px;
        z-index: 20;
        padding: 10px 18px 14px;
        background-color: var(--vc-paper-50);
        border-radius: 16px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    }

    .title-jersey-card-label{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: 0.65rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--vc-text-600);
        margin-bottom: 4px;
    }

    .title-logout{
        z-index: 30;
    }

    .title-jersey-card-name{
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: 0.8rem;
        color: var(--vc-ink-900);
        margin-top: 4px;
        max-width: 130px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>
